import { useRouter } from 'next/navigation';
import key from 'weak-key';

// styles
import styles from './cta-button.module.scss';

// components
import { ToolEmbed } from 'components/ToolEmbed/ToolEmbed';
import { buttonClick } from 'components/Button/trackingActions';

import { Button, Container } from '@geberit/gdds';
import { SectionClosingLine } from 'components/ContentElementsGdds/Common/section-closing-line';

//utils
import { useTracking } from 'utils/hooks/useTracking';
import { isEmpty } from 'utils/is-empty';
import { handlePopup } from 'utils/openInPopup';
import { decodingContent } from 'utils/decodingContent';
import { useSectionId } from 'utils/hooks/use-section-id';
import { useTracking as useTracking2 } from 'utils/tracking/track';
import { classNameBuilder } from 'utils/classNameBuilder';

type CtaLink = Link & { url: string; width?: number; height?: number };

type CTAButtonBase = {
  text: string;
  link: CtaLink;
  renderAsLink?: boolean;
  childOf?: string;
};

type CTAButtonGroupVariant = {
  isGroup: true;
  buttonStyle?: 'primary' | 'secondary';
  alignment?: string;
  contentIndex?: number;
  border?: string;
  titleInAnchor?: boolean;
};

type CTAButtonNonGroupVariant = {
  isGroup?: false;
  buttonStyle: 'primary' | 'secondary';
  alignment: string;
  contentIndex: number;
  border: string;
  titleInAnchor: boolean;
};

type CTAButtonProps = CTAButtonBase & (CTAButtonGroupVariant | CTAButtonNonGroupVariant);

const handleLinkClick = (e, windowTarget, target, router, link) => {
  e.preventDefault();

  if (windowTarget === '_self') {
    router.push(target);
  }

  if (windowTarget === '_blank' || windowTarget === 'default') {
    window.open(target, windowTarget)?.focus();
  }

  if (windowTarget === '_popup') {
    handlePopup(link, key(link));
  }
};

export function CTAButton({
  alignment = 'left',
  text,
  border,
  link,
  contentIndex,
  titleInAnchor,
  renderAsLink = true,
  buttonStyle = 'primary',
  isGroup = false,
  childOf,
  ...props
}: Readonly<CTAButtonProps>) {
  const track = useTracking();
  const router = useRouter();
  const { trackClick } = useTracking2();
  const sectionId = useSectionId('', '', titleInAnchor, contentIndex);
  const noSectionClosingLine = border === 'disable-border';

  if (isEmpty(link)) return null;

  const { type, url, showArrow, target, window: windowTarget } = link || {};
  const hasIframe = type === 'iframe';

  const trackOnClick = (e) => {
    if (!isEmpty(e.currentTarget.innerText)) {
      track.trackEvent(buttonClick(e.currentTarget.innerText));
      trackClick({
        click_intent: 'cta',
        click_element: 'button',
        click_text: e.currentTarget.innerText,
        click_url: link.target || link.url,
      });
    }
  };

  const onLinkClicked = (e) => {
    handleLinkClick(e, windowTarget, target, router, link);
    trackOnClick(e);
  };

  const iframeLightBoxStyle = {
    ...(link?.width && { maxWidth: `${link.width}px` }),
    ...(link?.height && { maxHeight: `${link.height}px` }),
  };

  const renderButton = () =>
    hasIframe ? (
      <ToolEmbed
        hasArrow={showArrow}
        src={url}
        label={decodingContent(text)}
        buttonStyle={buttonStyle}
        lightBoxStyle={iframeLightBoxStyle}
        useGddsButton
        stretched={false}
        {...props}
      />
    ) : (
      <Button
        stylingType={buttonStyle}
        onClick={onLinkClicked}
        {...(link.showArrow && { symbol: 'arrow_right' })}
        {...(renderAsLink && { as: 'a' })}
      >
        {decodingContent(text)}
      </Button>
    );

  return isGroup ? (
    renderButton()
  ) : (
    <>
      <section
        className={classNameBuilder(
          styles.ctaButtonWrapper,
          !noSectionClosingLine && styles['hasSCL'],
          childOf === 'accordion' && styles.marginIsAccordion,
          childOf === 'tab' && styles.marginIsTab,
        )}
      >
        <Container id={sectionId}>
          <div className={classNameBuilder(`grid-x align-${alignment}`, styles.ctaButton)}>
            {renderButton()}
          </div>
        </Container>
      </section>
      {!noSectionClosingLine && <SectionClosingLine />}
    </>
  );
}
